import { OnboardingLayout } from '@/components/OnboardingLayout'
import { TokenError } from '@/components/TokenError'
import { useCreateAccount } from '@/hooks/mutations/onboarding/useCreateAccount'
import { getErrorMessage } from '@/lib/queries'
import { mapTokenErrorMessage, type TokenErrorReason } from '@/lib/tokenErrors'
import { getLocale } from '@/stores/i18n'
import { setSessionToken, setTenant } from '@/stores/session'
import { MESSAGE, ONBOARDING_FLOW_KEYS, type OnboardingFlowKey } from '@repo/constants'
import { Button, Card, CardContent } from '@repo/ui/components'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeftIcon, LoaderCircleIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const searchSchema = z.object({
  email: z.string().default(''),
  firstName: z.string().default(''),
  lastName: z.string().default(''),
  phone: z.string().optional(),
})

export const Route = createFileRoute('/onboard/$flow/$token/create-account')({
  validateSearch: searchSchema,
  component: CreateAccountPage,
})

function isFlowKey(v: string): v is OnboardingFlowKey {
  return (ONBOARDING_FLOW_KEYS as readonly string[]).includes(v)
}

function CreateAccountPage() {
  const { flow: rawFlow, token } = Route.useParams()
  const { email, firstName, lastName, phone } = Route.useSearch()
  const navigate = useNavigate()
  const { t } = useTranslation('ui')
  const createAccount = useCreateAccount()
  const [errorState, setErrorState] = useState<
    { kind: 'emailExists' } | { kind: 'other'; message: string } | null
  >(null)
  const [tokenError, setTokenError] = useState<TokenErrorReason | null>(null)

  const flow = isFlowKey(rawFlow) ? rawFlow : null
  if (!flow) return <TokenError reason="not_found" />
  if (tokenError) return <TokenError reason={tokenError} />

  const emailExists = errorState?.kind === 'emailExists'
  // A raw backend code must never reach the banner: the server gate answers
  // `ONBOARDING_APPLICATION_TERMINAL` here whenever the office rejects while the
  // form is open, which is the gate's normal path. Keyed by code with the code
  // itself as the fallback, so an unmapped one degrades to the old behaviour.
  const resolveErrorMessage = () => {
    if (errorState?.kind === 'emailExists') return t('onboarding.error.emailExists')
    if (!errorState?.message) return null
    return t(`onboarding.error.${errorState.message}`, { defaultValue: errorState.message })
  }
  const errorMessage = resolveErrorMessage()

  const handleCreateAccount = () => {
    setErrorState(null)
    createAccount.mutate(
      {
        flow,
        token,
        email,
        firstName,
        lastName,
        phone: phone || undefined,
        preferredLanguage: getLocale(),
      },
      {
        onSuccess: (data) => {
          // A brand-new account comes back with a live session: adopt it and
          // continue into the wizard. setSessionToken must land before navigate —
          // the api client is derived from that atom, so the _auth guard the
          // transition triggers needs the new bearer token already in the store.
          const session = 'session' in data ? data.session : null
          if (session) {
            setSessionToken(session.sessionToken)
            setTenant(session.tenant)
            navigate({
              to: '/carriers/$slug/driver-onboarding/onboarding',
              params: { slug: session.tenant.slug },
            })
            return
          }
          const message = 'message' in data ? data.message : null
          const getReason = () => {
            if (message === MESSAGE.JOIN_CONFIRMATION_SENT) return 'join_confirmation_sent'
            // ACCOUNT_CREATED without a session: the account is new but auto-login
            // failed, so the backend fell back to emailing the magic link.
            if (message === MESSAGE.ACCOUNT_CREATED) return 'account_created_login_link_sent'
            return 'login_link_sent'
          }
          const reason = getReason()
          navigate({
            to: '/onboard/$flow/$token/success',
            params: { flow, token },
            search: { reason },
          })
        },
        onError: (err: unknown) => {
          const reason = mapTokenErrorMessage(err)
          if (reason) {
            setTokenError(reason)
            return
          }
          const message = getErrorMessage(err, '')
          if (message === MESSAGE.DRIVER_EMAIL_EXISTS) {
            setErrorState({ kind: 'emailExists' })
          } else if (message) {
            setErrorState({ kind: 'other', message })
          }
        },
      },
    )
  }

  return (
    <OnboardingLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-bold">{t('onboarding.createAccount.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('onboarding.createAccount.subtitle')}
          </p>
        </div>

        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-3">
              <div className="text-sm">
                <span className="text-muted-foreground">{t('onboarding.firstName')}:</span>{' '}
                <span className="font-medium">{firstName}</span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">{t('onboarding.lastName')}:</span>{' '}
                <span className="font-medium">{lastName}</span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">
                  {t('onboarding.createAccount.emailLabel')}:
                </span>{' '}
                <span className="font-medium">{email}</span>
              </div>
              {phone && (
                <div className="text-sm">
                  <span className="text-muted-foreground">{t('onboarding.phone')}:</span>{' '}
                  <span className="font-medium">{phone}</span>
                </div>
              )}
            </div>

            {errorMessage && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {errorMessage}
              </div>
            )}
          </CardContent>
        </Card>

        {emailExists ? (
          <div className="flex flex-col gap-6">
            <Link to="/login" search={{ email }}>
              <Button className="w-full">{t('onboarding.createAccount.goToLogin')}</Button>
            </Link>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/onboard/$flow/$token" params={{ flow, token }}>
                <Button variant="outline" className="w-full">
                  <ArrowLeftIcon className="size-4" />
                  {t('onboarding.back')}
                </Button>
              </Link>
              <Button
                variant="outline"
                className="w-full"
                onPress={handleCreateAccount}
                isDisabled={createAccount.isPending}
              >
                {createAccount.isPending && <LoaderCircleIcon className="animate-spin" />}
                {t('onboarding.createAccount.tryAgain')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Button
              className="w-full"
              onPress={handleCreateAccount}
              isDisabled={createAccount.isPending}
            >
              {createAccount.isPending && <LoaderCircleIcon className="animate-spin" />}
              {t('onboarding.createAccount.submit')}
            </Button>
            <Link to="/onboard/$flow/$token" params={{ flow, token }}>
              <Button variant="ghost" className="w-full">
                <ArrowLeftIcon className="size-4" />
                {t('onboarding.back')}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </OnboardingLayout>
  )
}
